/**
 * The Home field of the card Customize dialog: 1.AC-2 deliverable 18's combobox over
 * `search.picker` (canon pages only, eight hits, Arrow keys and Enter), copied because 1.AC-2's
 * module does not export it; the pick shows as a `TypeChip` with "Clear home".
 */
import { Button, TYPE_KEYS, TypeChip, type TypeKey } from '@codex/ui';
import { useEffect, useId, useState } from 'react';
import { useHomeHits } from '../../../sheet-form/data/variant.js';

export interface HomePick {
  readonly id: string;
  readonly title: string;
  readonly typeKey: string;
}

/** A user type the design has no glyph for shows the note glyph. */
const glyphFor = (typeKey: string): TypeKey =>
  (TYPE_KEYS as readonly string[]).includes(typeKey) ? (typeKey as TypeKey) : 'note';

export function HomeField({
  home,
  onPick,
  error,
}: {
  readonly home: HomePick | null;
  readonly onPick: (home: HomePick | null) => void;
  readonly error: string | undefined;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const errorId = `${id}-error`;
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), 150);
    return () => clearTimeout(timer);
  }, [text]);
  const hits = useHomeHits(query, home === null && query !== '');
  const list = text.trim() === query && query !== '' ? (hits.data ?? []) : [];
  const current = Math.min(active, Math.max(list.length - 1, 0));
  const invalid = error !== undefined || undefined;
  const describedBy = error !== undefined ? errorId : undefined;
  const choose = (index: number) => {
    const hit = list[index];
    if (!hit) return;
    onPick({ id: hit.id, title: hit.title, typeKey: hit.typeKey });
    setText('');
    setQuery('');
  };
  const message = error !== undefined && (
    <span id={errorId} className="cx-cards__issue">
      {error}
    </span>
  );
  if (home !== null) {
    return (
      <div className="cx-field">
        <span className="cx-field__label">Home</span>
        <div className="cx-cards__controls">
          <TypeChip type={glyphFor(home.typeKey)} title={home.title} />
          <Button
            size="sm"
            variant="ghost"
            aria-invalid={invalid}
            aria-describedby={describedBy}
            onClick={() => onPick(null)}
          >
            Clear home
          </Button>
        </div>
        {message}
      </div>
    );
  }
  const step = (by: number) => setActive((current + by + list.length) % list.length);
  return (
    <div className="cx-field">
      <label htmlFor={id} className="cx-field__label">
        Home
      </label>
      <span className="cx-input" data-control-boundary="">
        <input
          id={id}
          type="text"
          role="combobox"
          className="cx-input__el"
          aria-expanded={list.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={list.length > 0 ? `${listId}-${current}` : undefined}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          placeholder="Optional: a page to put the copy in"
          value={text}
          onChange={(event) => {
            setText(event.currentTarget.value);
            setActive(0);
          }}
          onKeyDown={(event) => {
            if (list.length === 0) return;
            if (event.key === 'Enter') choose(current);
            else if (event.key === 'ArrowDown') step(1);
            else if (event.key === 'ArrowUp') step(-1);
            else return;
            event.preventDefault();
          }}
        />
      </span>
      <div id={listId} role="listbox" aria-label="Home results" hidden={list.length === 0}>
        {list.map((hit, index) => (
          // biome-ignore lint/a11y/useKeyWithClickEvents: the combobox input owns the keyboard
          <div
            key={hit.id}
            id={`${listId}-${index}`}
            role="option"
            tabIndex={-1}
            aria-selected={index === current}
            className="cx-cards__option"
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => choose(index)}
          >
            {hit.title}
          </div>
        ))}
      </div>
      {message}
    </div>
  );
}
